export interface RiskData {
  learner: string;
  course: string;
  progress: number;
  expected: number;
  last: string;
  flags: string[];
  actionNeeded: string;
  daysOverdue?: number;
  daysUntilDeadline?: number;
  riskScore: number;
  riskCategory: 'critical' | 'high' | 'medium' | 'approaching';
  deadline?: string;
}

export interface DeadlineData {
  course: string;
  cohort: string;
  end: string;
  count: number;
}

export interface EffectivenessData {
  course: string;
  pre: number;
  post: number;
  csat: number;
}

export interface SkillGapData {
  learnerName: string;
  userId: number;
  competency: string;
  currentProficiency: number;
  targetProficiency: number;
  gap: number;
  severity: 'Critical' | 'High' | 'Medium';
}

export interface CompetencyGapSummary {
  competency: string;
  totalLearners: number;
  learnersWithGap: number;
  gapPercentage: number;
  avgProficiency: number;
  needsAttention: boolean; // Deprecated - use status instead
  status: 'Critical' | 'Needs Attention' | 'Monitor' | 'On Track';
}

export interface LearnerSkillProfile {
  userId: number;
  learnerName: string;
  competencies: {
    competency: string;
    baselinePercentage?: number | null;
    endlinePercentage?: number | null;
    percentage: number | null;
    learningGain?: number | null;
    score: number | null;
    total: number | null;
  }[];
  overallProficiency: number;
  averageLearningGain?: number;
}

export interface LearningGainData {
  learnerName: string;
  userId: number;
  competency: string;
  baselinePercentage: number;
  endlinePercentage: number;
  learningGain: number;
  gainCategory: 'Excellent' | 'Good' | 'Moderate' | 'Minimal' | 'Declined';
}

export interface LmsAnalyticsData {
  learners: number;
  overdue: number;
  programs: number;
  deadlines: number;
  learningDonut: number[];
  programDonut: number[];
  completionRates: {
    labels: string[];
    values: number[];
  };
  training: {
    labels: string[];
    values: number[];
  };
  enrollmentsMonthly: {
    labels: string[];
    values: number[];
  };
  programHealth: {
    labels: string[];
    values: number[];
    isTopPerformer: boolean[];
  };
  risks: RiskData[];
  deadlinesRows: DeadlineData[];
  competencies: string[];
  courses: string[];
  competencyWeights: number[][];
  prePost: {
    pre: number;
    post: number;
  };
  csat: number;
  effectivenessByCourse: EffectivenessData[];
  // Skill gap analysis data
  skillGaps?: SkillGapData[];
  competencyGapSummary?: CompetencyGapSummary[];
  learnerSkillMatrix?: LearnerSkillProfile[];
  learningGains?: LearningGainData[];
  // Raw records for modal display
  rawRecords?: {
    allEnrollments?: any[];
    lessonPlans?: any[];
    tags?: any[];
    uniqueLearnerRecords?: any[];
    overdueRecords?: any[];
    activeProgramRecords?: any[];
    upcomingDeadlineRecords?: any[];
    completedRecords?: any[];
    startedRecords?: any[];
    assignedRecords?: any[];
    programCohortsFullyCompleted?: any[];
    programCohortsInProgress?: any[];
    programCohortsNotStarted?: any[];
  };
}

// Static JSON data that powers the dashboard
export const STATIC_DATA: LmsAnalyticsData = {
  learners: 5,
  overdue: 2,
  programs: 4,
  deadlines: 1,
  learningDonut: [40, 30, 20, 10], // Completed, In Progress, Not Started, Overdue
  programDonut: [25, 50, 25], // Completed, In Progress, Not Started
  completionRates: {
    labels: ['Checkpoint', 'Course', 'Live Training', 'Program', 'Survey'],
    values: [28, 71, 48, 69, 100],
  },
  training: {
    labels: ['Enrollments', 'Registrations', 'Completed'],
    values: [62, 34, 30],
  },
  enrollmentsMonthly: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    values: [60, 22, 310, 480, 120, 390],
  },
  programHealth: {
    labels: ['Ops Essentials', 'New Hire', 'CX Academy', 'Sales Pro'],
    values: [48, 47, 46, 49],
    isTopPerformer: [true, true, false, false],
  },
  risks: [
    {
      learner: 'Asha Iyer',
      course: 'Workplace Safety',
      progress: 20,
      expected: 55,
      last: '2025-10-02',
      flags: ['Inactive'],
      actionNeeded: 'Re-engage learner',
      daysOverdue: 5,
      riskScore: 45,
      riskCategory: 'medium',
    },
    {
      learner: 'Rina Paul',
      course: 'Workplace Safety',
      progress: 5,
      expected: 55,
      last: '2025-09-25',
      flags: ['Inactive'],
      actionNeeded: 'Urgent: Contact learner',
      daysOverdue: 12,
      riskScore: 68,
      riskCategory: 'high',
    },
  ],
  deadlinesRows: [
    {
      course: 'Data Privacy Basics',
      cohort: 'Onboarding',
      end: '2025-10-18',
      count: 1
    },
  ],
  competencies: ['Data Privacy', 'Safety', 'Service Quality', 'Selling Skills'],
  courses: [
    'Workplace Safety',
    'Data Privacy Basics',
    'Customer Excellence',
    'Consultative Selling',
  ],
  competencyWeights: [
    [0, 100, 20, 0],    // Data Privacy
    [100, 0, 0, 0],     // Safety
    [0, 0, 80, 0],      // Service Quality
    [0, 0, 0, 100],     // Selling Skills
  ],
  prePost: { pre: 62, post: 81 },
  csat: 72,
  effectivenessByCourse: [
    { course: 'Workplace Safety', pre: 55, post: 78, csat: 95 },
    { course: 'Data Privacy Basics', pre: 60, post: 85, csat: 55 },
    { course: 'Customer Excellence', pre: 65, post: 80, csat: 82 },
    { course: 'Consultative Selling', pre: 58, post: 82, csat: 70 },
  ],
};
